import { create } from 'zustand';
import type { BootstrapPayload, Session, User } from '../types/auth';
import { SESSION_STORAGE_KEY } from '../services/config';

type SessionState = {
  session: Session | null;
  user: User | null;
  bootstrap: BootstrapPayload | null;
  hydrated: boolean;
  activeTenantId: string | null;
  activeTenantName: string | null;
  activeTenantLogo: string | null;
  setSession: (session: Session | null) => void;
  setBootstrap: (bootstrap: BootstrapPayload | null) => void;
  setActiveTenant: (tenantId: string | null, tenantName?: string | null, tenantLogo?: string | null) => void;
  setActiveTenantLogo: (logoUrl: string | null) => void;
  hydrate: () => void;
  clear: () => void;
};

const ACTIVE_TENANT_ID_KEY = 'crm_active_tenant_id';
const ACTIVE_TENANT_NAME_KEY = 'crm_active_tenant_name';
const ACTIVE_TENANT_LOGO_KEY = 'crm_active_tenant_logo';

export function isTokenExpired(token?: string | null): boolean {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return false;
    // For 2-part tokens (body.signature), body is parts[0]. For 3-part JWTs (header.body.signature), body is parts[1].
    const base64Url = parts.length === 2 ? parts[0] : parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const parsed = JSON.parse(jsonPayload);
    if (parsed && typeof parsed.exp === 'number') {
      const expMs = parsed.exp > 100000000000 ? parsed.exp : parsed.exp * 1000;
      // 10 second safety buffer for clock drift
      return Date.now() >= expMs - 10000;
    }
  } catch {
    // If decoding fails, let server-side validation decide
    return false;
  }
  return false;
}

const readStoredSession = (): Session | null => {
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as Session;
    if (!session?.access_token || isTokenExpired(session.access_token)) {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
};

const persistSession = (session: Session | null) => {
  if (!session) {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
};

export function normalizeTenantId(raw?: string | null): string | null {
  if (!raw) return null;
  const first = String(raw).split(',')[0].trim();
  if (
    first === 'Inteligência e Negócios Tecnologia' ||
    first === 'Inteligencia e Negocios Tecnologia' ||
    first === 'inteligencia-e-negocios' ||
    first === 'vanguarda'
  ) {
    return 'vanguarda';
  }
  return first;
}

export const useSessionStore = create<SessionState>((set) => ({
  session: null,
  user: null,
  bootstrap: null,
  hydrated: false,
  activeTenantId: null,
  activeTenantName: null,
  activeTenantLogo: null,
  setSession: (session) => {
    persistSession(session);
    const rawStoredId = window.localStorage.getItem(ACTIVE_TENANT_ID_KEY);
    const activeTenantId = normalizeTenantId(rawStoredId);
    if (rawStoredId && activeTenantId !== rawStoredId) {
      if (activeTenantId) window.localStorage.setItem(ACTIVE_TENANT_ID_KEY, activeTenantId);
      else window.localStorage.removeItem(ACTIVE_TENANT_ID_KEY);
    }
    const activeTenantName = window.localStorage.getItem(ACTIVE_TENANT_NAME_KEY) || (activeTenantId === 'vanguarda' ? 'Inteligência e Negócios Tecnologia' : null);
    const activeTenantLogo = window.localStorage.getItem(ACTIVE_TENANT_LOGO_KEY);
    const userTenantId = normalizeTenantId(session?.user?.tenant_id) || 'vanguarda';
    const updatedUser = session?.user
      ? {
          ...session.user,
          tenant_id: userTenantId,
          active_tenant_id: activeTenantId || userTenantId,
          active_tenant_name: activeTenantName || session.user.tenant_name || 'Inteligência e Negócios Tecnologia',
          logo_url: activeTenantLogo || session.user.logo_url || null,
        }
      : null;
    set({
      session,
      user: updatedUser,
      activeTenantId: activeTenantId || userTenantId,
      activeTenantName: activeTenantName || session?.user?.tenant_name || 'Inteligência e Negócios Tecnologia',
      activeTenantLogo: activeTenantLogo || session?.user?.logo_url || null,
    });
  },
  setBootstrap: (bootstrap) => {
    const rawStoredId = window.localStorage.getItem(ACTIVE_TENANT_ID_KEY);
    const activeTenantId = normalizeTenantId(rawStoredId);
    if (rawStoredId && activeTenantId !== rawStoredId) {
      if (activeTenantId) window.localStorage.setItem(ACTIVE_TENANT_ID_KEY, activeTenantId);
      else window.localStorage.removeItem(ACTIVE_TENANT_ID_KEY);
    }
    const activeTenantName = window.localStorage.getItem(ACTIVE_TENANT_NAME_KEY) || (activeTenantId === 'vanguarda' ? 'Inteligência e Negócios Tecnologia' : null);
    const activeTenantLogo = window.localStorage.getItem(ACTIVE_TENANT_LOGO_KEY);
    const resolvedLogo = activeTenantLogo || bootstrap?.tenant?.logo_url || bootstrap?.user?.logo_url || null;
    const userTenantId = normalizeTenantId(bootstrap?.user?.tenant_id) || 'vanguarda';
    const updatedUser = bootstrap?.user
      ? {
          ...bootstrap.user,
          tenant_id: userTenantId,
          active_tenant_id: activeTenantId || userTenantId,
          active_tenant_name: activeTenantName || bootstrap.user.tenant_name || 'Inteligência e Negócios Tecnologia',
          logo_url: resolvedLogo,
        }
      : null;
    set({
      bootstrap,
      user: updatedUser,
      activeTenantId: activeTenantId || userTenantId,
      activeTenantName: activeTenantName || bootstrap?.user?.tenant_name || 'Inteligência e Negócios Tecnologia',
      activeTenantLogo: resolvedLogo,
    });
  },
  setActiveTenant: (rawTenantId, rawTenantName, tenantLogo) => {
    const tenantId = normalizeTenantId(rawTenantId);
    const tenantName = rawTenantName || (tenantId === 'vanguarda' ? 'Inteligência e Negócios Tecnologia' : null);
    if (tenantId) {
      window.localStorage.setItem(ACTIVE_TENANT_ID_KEY, tenantId);
      if (tenantName) {
        window.localStorage.setItem(ACTIVE_TENANT_NAME_KEY, tenantName);
      }
      if (tenantLogo) {
        window.localStorage.setItem(ACTIVE_TENANT_LOGO_KEY, tenantLogo);
      }
    } else {
      window.localStorage.removeItem(ACTIVE_TENANT_ID_KEY);
      window.localStorage.removeItem(ACTIVE_TENANT_NAME_KEY);
      window.localStorage.removeItem(ACTIVE_TENANT_LOGO_KEY);
    }
    set((state) => {
      const userTenantId = normalizeTenantId(state.user?.tenant_id) || 'vanguarda';
      const updatedUser = state.user
        ? {
            ...state.user,
            tenant_id: userTenantId,
            active_tenant_id: tenantId || userTenantId,
            active_tenant_name: tenantName || state.user.tenant_name || 'Inteligência e Negócios Tecnologia',
            logo_url: tenantLogo !== undefined ? tenantLogo : state.user.logo_url,
          }
        : null;
      return {
        activeTenantId: tenantId,
        activeTenantName: tenantName || (tenantId ? tenantId : null),
        activeTenantLogo: tenantLogo !== undefined ? tenantLogo : state.activeTenantLogo,
        user: updatedUser,
      };
    });
  },
  setActiveTenantLogo: (logoUrl) => {
    if (logoUrl) {
      window.localStorage.setItem(ACTIVE_TENANT_LOGO_KEY, logoUrl);
    } else {
      window.localStorage.removeItem(ACTIVE_TENANT_LOGO_KEY);
    }
    set((state) => {
      if (state.activeTenantLogo === logoUrl && state.user?.logo_url === logoUrl) {
        return state;
      }
      const updatedUser = state.user
        ? { ...state.user, logo_url: logoUrl }
        : null;
      const updatedBootstrap = state.bootstrap
        ? {
            ...state.bootstrap,
            user: updatedUser || state.bootstrap.user,
            tenant: state.bootstrap.tenant
              ? { ...state.bootstrap.tenant, logo_url: logoUrl }
              : undefined,
          }
        : null;
      return {
        activeTenantLogo: logoUrl,
        user: updatedUser,
        bootstrap: updatedBootstrap,
      };
    });
  },
  hydrate: () => {
    const session = readStoredSession();
    const rawStoredId = window.localStorage.getItem(ACTIVE_TENANT_ID_KEY);
    const activeTenantId = normalizeTenantId(rawStoredId);
    if (rawStoredId && activeTenantId !== rawStoredId) {
      if (activeTenantId) window.localStorage.setItem(ACTIVE_TENANT_ID_KEY, activeTenantId);
      else window.localStorage.removeItem(ACTIVE_TENANT_ID_KEY);
    }
    const activeTenantName = window.localStorage.getItem(ACTIVE_TENANT_NAME_KEY) || (activeTenantId === 'vanguarda' ? 'Inteligência e Negócios Tecnologia' : null);
    const activeTenantLogo = window.localStorage.getItem(ACTIVE_TENANT_LOGO_KEY);
    const userTenantId = normalizeTenantId(session?.user?.tenant_id) || 'vanguarda';
    const updatedUser = session?.user
      ? {
          ...session.user,
          tenant_id: userTenantId,
          active_tenant_id: activeTenantId || userTenantId,
          active_tenant_name: activeTenantName || session.user.tenant_name || 'Inteligência e Negócios Tecnologia',
          logo_url: activeTenantLogo || session.user.logo_url || null,
        }
      : null;
    set({
      session,
      user: updatedUser,
      activeTenantId: activeTenantId || userTenantId,
      activeTenantName: activeTenantName || session?.user?.tenant_name || 'Inteligência e Negócios Tecnologia',
      activeTenantLogo: activeTenantLogo || session?.user?.logo_url || null,
      hydrated: true,
    });
  },
  clear: () => {
    persistSession(null);
    window.localStorage.removeItem(ACTIVE_TENANT_ID_KEY);
    window.localStorage.removeItem(ACTIVE_TENANT_NAME_KEY);
    window.localStorage.removeItem(ACTIVE_TENANT_LOGO_KEY);
    set({
      session: null,
      user: null,
      bootstrap: null,
      activeTenantId: null,
      activeTenantName: null,
      activeTenantLogo: null,
      hydrated: true,
    });
  },
}));
